import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

export const DEFAULT_DOCUMENT_TITLE = 'AGMS CRM'

/**
 * Fixed tab titles for every route. Detail pages use the page type so a
 * business name, case id, ticket number, or article title never reaches
 * the tab or browser history.
 */
const PAGE_TITLES: Array<[RegExp, string]> = [
  [/^\/login$/, 'Sign in'],
  [/^\/forgot-password$/, 'Forgot password'],
  [/^\/reset-password$/, 'Reset your password'],
  [/^\/first-login$/, 'Set your password'],
  [/^\/setup-2fa$/, 'Set up two-factor authentication'],
  [/^\/upload\/documents\/[^/]+$/, DEFAULT_DOCUMENT_TITLE],
  [/^\/upload\/[^/]+$/, DEFAULT_DOCUMENT_TITLE],
  [/^\/dashboard$/, 'Dashboard'],
  [/^\/leads\/new$/, 'New Lead'],
  [/^\/leads\/[^/]+\/application$/, 'TurboApp'],
  [/^\/leads\/[^/]+$/, 'Lead'],
  [/^\/leads$/, 'Leads'],
  [/^\/appointments$/, 'Appointments'],
  [/^\/pipeline$/, 'Deployment Center'],
  [/^\/account-groups\/[^/]+$/, 'Account group'],
  [/^\/account-groups$/, 'Account groups'],
  [/^\/merchants\/[^/]+$/, 'Merchant'],
  [/^\/merchants$/, 'Merchants'],
  [/^\/chargebacks\/[^/]+$/, 'Chargeback'],
  [/^\/chargebacks$/, 'Chargebacks'],
  [/^\/tickets\/stats$/, 'Ticket accountability'],
  [/^\/tickets\/[^/]+$/, 'Ticket'],
  [/^\/tickets$/, 'Tickets'],
  [/^\/help\/new$/, 'New Help Article'],
  [/^\/help\/review$/, 'Article Queue'],
  [/^\/help\/categories$/, 'Help Categories'],
  [/^\/help\/gaps$/, 'Knowledge gaps'],
  [/^\/help\/health$/, 'Article health'],
  [/^\/help\/[^/]+\/edit$/, 'Edit Help Article'],
  [/^\/help\/[^/]+$/, 'Help article'],
  [/^\/help$/, 'Help Hub'],
  [/^\/inbox$/, 'Inbox'],
  [/^\/reports$/, 'Reports'],
  [/^\/settings\/users\/new$/, 'New User'],
  [/^\/settings\/users\/[^/]+$/, 'Edit User'],
  [/^\/settings\/users$/, 'Users'],
  [/^\/settings\/pricing-templates$/, 'Pricing Templates'],
  [/^\/settings\/schedule-a$/, 'Schedule A'],
  [/^\/settings\/interchange-reviews$/, 'Interchange document review'],
  [/^\/settings\/ticket-types\/new$/, 'New Ticket Type'],
  [/^\/settings\/ticket-types\/[^/]+$/, 'Edit Ticket Type'],
  [/^\/settings\/ticket-types$/, 'Ticket Types'],
  [/^\/settings$/, 'Settings'],
  [/^\/statement-analysis\/[^/]+$/, 'Statement analysis'],
  [/^\/statement-analysis$/, 'Statement Analysis'],
]

export function documentTitleForPath(pathname: string): string {
  const path = normalizePath(pathname)
  if (path === '/') {
    return DEFAULT_DOCUMENT_TITLE
  }

  const page = PAGE_TITLES.find(([pattern]) => pattern.test(path))?.[1]
  if (!page) {
    return `Page not found · ${DEFAULT_DOCUMENT_TITLE}`
  }
  if (page === DEFAULT_DOCUMENT_TITLE) {
    return DEFAULT_DOCUMENT_TITLE
  }

  return `${page} · ${DEFAULT_DOCUMENT_TITLE}`
}

function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split('?')[0]?.split('#')[0] ?? '/'
  if (withoutQuery.length > 1 && withoutQuery.endsWith('/')) {
    return withoutQuery.slice(0, -1)
  }

  return withoutQuery || '/'
}

export function DocumentTitle() {
  const { pathname } = useLocation()

  useLayoutEffect(() => {
    document.title = documentTitleForPath(pathname)
  }, [pathname])

  return null
}
